import { resolveBrandingDisplayName } from "@lifter/hub-ui/browser-shared";
import { useState } from "react";
import { cn } from "../../lib/cn";
import { LIFTER_WORDMARK_VIEWBOX } from "./lifter-wordmark-sprite";

type WordmarkSize = "sidebar" | "account" | "mini" | "topbar";

interface BrandWordmarkProps {
  branding: {
    slug?: string | null;
    displayName: string | null;
    logoUrl?: string | null;
  };
  size?: WordmarkSize;
  className?: string;
}

// `px` mirrors the CSS height so the <img> can carry width/height attributes.
const SIZE_STYLES: Record<WordmarkSize, { height: string; px: number; text: string }> = {
  sidebar: { height: "h-(--space-7)", px: 28, text: "text-[length:var(--type-h3)]" },
  account: { height: "h-(--space-10)", px: 40, text: "text-[length:var(--type-h2)]" },
  mini: { height: "h-3.5", px: 14, text: "text-[length:var(--type-eyebrow)]" },
  topbar: { height: "h-(--space-6)", px: 24, text: "text-[length:var(--type-h4)]" },
};

export function BrandWordmark({ branding, size = "sidebar", className }: BrandWordmarkProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const displayName = resolveBrandingDisplayName({ displayName: branding.displayName });
  const isLifter = branding.slug === "lifter" || branding.slug === "dev";
  const { height, px, text } = SIZE_STYLES[size];
  const showNameWithLogo = size === "sidebar" || size === "topbar";
  const displayNameClassName = cn(
    "font-display block min-w-0 truncate font-extrabold tracking-(--type-tracking-display) text-(--color-text)",
    text,
  );

  if (isLifter) {
    return (
      <svg
        aria-label={displayName}
        className={cn(height, "w-auto", className)}
        role="img"
        viewBox={LIFTER_WORDMARK_VIEWBOX}
      >
        <use href="#logo-lifter" />
      </svg>
    );
  }

  if (branding.logoUrl && !imgFailed) {
    const logo = (
      <img
        alt={showNameWithLogo ? "" : displayName}
        className={cn(
          height,
          "object-contain",
          showNameWithLogo ? "aspect-square shrink-0" : "w-auto max-w-full",
          !showNameWithLogo && className,
        )}
        height={px}
        onError={() => setImgFailed(true)}
        src={branding.logoUrl}
        width={px}
      />
    );

    if (showNameWithLogo) {
      return (
        <div className={cn("flex min-w-0 items-center gap-(--space-2)", className)}>
          {logo}
          <span className={displayNameClassName} title={displayName}>
            {displayName}
          </span>
        </div>
      );
    }

    return logo;
  }

  return (
    <span className={cn(displayNameClassName, className)} title={displayName}>
      {displayName}
    </span>
  );
}
